import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calendar, Clock, ArrowLeft, Tag, ChevronRight, Newspaper, Share2 } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { mockNews } from '@/data/news-blogs-data'

function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
}

// Simple markdown-like renderer for the content field
function renderContent(content: string) {
    const lines = content.split('\n')
    return lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-3" />

        // Bold headers like **Title:**
        if (line.startsWith('**') && line.endsWith('**')) {
            const text = line.slice(2, -2)
            return <h3 key={i} className="text-lg font-bold text-theme-primary mt-6 mb-2">{text}</h3>
        }

        // Emoji bullets or numbered lists
        if (/^[🔴🟡🟢]/.test(line)) {
            const clean = line.replace(/\*\*(.*?)\*\*/g, (_m, p1) => p1)
            return (
                <p key={i} className="font-semibold text-theme-primary mt-4 mb-1">
                    {clean}
                </p>
            )
        }

        // Numbered lists
        if (/^\d+\./.test(line)) {
            const withBold = line.split(/\*\*(.*?)\*\*/g)
            return (
                <li key={i} className="text-theme-secondary ml-4 mb-1 list-decimal">
                    {withBold.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-theme-primary">{part}</strong> : part)}
                </li>
            )
        }

        // Bullet lists
        if (line.startsWith('- ')) {
            const text = line.slice(2)
            const withBold = text.split(/\*\*(.*?)\*\*/g)
            return (
                <li key={i} className="text-theme-secondary ml-4 mb-1 list-disc">
                    {withBold.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-theme-primary">{part}</strong> : part)}
                </li>
            )
        }

        // Regular paragraph with inline bold
        const withBold = line.split(/\*\*(.*?)\*\*/g)
        return (
            <p key={i} className="text-theme-secondary leading-relaxed mb-2">
                {withBold.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-theme-primary font-semibold">{part}</strong> : part)}
            </p>
        )
    })
}

export default function NewsDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const article = mockNews.find(n => n.id === id)

    if (!article) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <GlassmorphicCard className="p-12 text-center max-w-md">
                    <Newspaper className="h-12 w-12 text-theme-muted mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-theme-primary mb-2">Article Not Found</h2>
                    <p className="text-theme-secondary mb-6">This news article doesn't exist or may have been removed.</p>
                    <Link to="/news" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-blue text-white font-semibold rounded-xl">
                        <ArrowLeft className="h-4 w-4" /> Back to News
                    </Link>
                </GlassmorphicCard>
            </div>
        )
    }

    const related = mockNews.filter(n => n.id !== article.id && n.category === article.category).slice(0, 3)
    const otherCategories = [...new Set(mockNews.map(n => n.category))].filter(c => c !== article.category)

    return (
        <div className="min-h-screen relative">
            {/* Hero image banner */}
            <div className="relative h-72 md:h-96 overflow-hidden">
                <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-12 container mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="max-w-3xl"
                    >
                        <div className="flex flex-wrap gap-2 mb-3">
                            <span className="px-3 py-1 bg-primary-blue text-white text-xs font-semibold rounded-full">{article.category}</span>
                            <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">{article.tag}</span>
                        </div>
                        <h1 className="text-2xl md:text-4xl font-display font-bold text-white leading-tight">{article.title}</h1>
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-white/80 text-sm">
                            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{formatDate(article.date)}</span>
                            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{article.readTime}</span>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Breadcrumb */}
            <div className="container mx-auto px-4 pt-6">
                <div className="flex items-center gap-2 text-sm text-theme-muted mb-6">
                    <Link to="/" className="hover:text-primary-blue transition-colors">Home</Link>
                    <ChevronRight className="h-3.5 w-3.5" />
                    <Link to="/news" className="hover:text-primary-blue transition-colors">News</Link>
                    <ChevronRight className="h-3.5 w-3.5" />
                    <span className="text-theme-secondary truncate max-w-xs">{article.title}</span>
                </div>
            </div>

            <section className="pb-16">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Article content */}
                        <div className="lg:col-span-2">
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <GlassmorphicCard className="p-6 md:p-10">
                                    {/* Excerpt lead */}
                                    <p className="text-base md:text-lg text-theme-secondary font-medium leading-relaxed mb-6 pb-6 border-b border-slate-200 dark:border-slate-700">
                                        {article.excerpt}
                                    </p>
                                    {/* Full content */}
                                    <div className="prose-like">
                                        {renderContent(article.content)}
                                    </div>

                                    {/* Share row */}
                                    <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between flex-wrap gap-3">
                                        <div className="flex items-center gap-2 text-sm text-theme-muted">
                                            <Tag className="h-4 w-4" />
                                            <span className="px-2.5 py-1 bg-primary-blue/10 text-primary-blue rounded-full">{article.category}</span>
                                            <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-theme-secondary rounded-full">{article.tag}</span>
                                        </div>
                                        <button
                                            onClick={() => navigator.share ? navigator.share({ title: article.title, url: window.location.href }) : undefined}
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                        >
                                            <Share2 className="h-4 w-4" /> Share
                                        </button>
                                    </div>
                                </GlassmorphicCard>

                                {/* Back button */}
                                <button
                                    onClick={() => navigate(-1)}
                                    className="mt-6 inline-flex items-center gap-2 text-sm text-theme-secondary hover:text-primary-blue transition-colors"
                                >
                                    <ArrowLeft className="h-4 w-4" /> Back to News
                                </button>
                            </motion.div>

                            {/* Related articles */}
                            {related.length > 0 && (
                                <div className="mt-10">
                                    <h2 className="text-xl font-bold text-theme-primary mb-4">Related in {article.category}</h2>
                                    <div className="space-y-4">
                                        {related.map(r => (
                                            <Link key={r.id} to={`/news/${r.id}`}>
                                                <GlassmorphicCard className="overflow-hidden group cursor-pointer hover:shadow-lg transition-all duration-300">
                                                    <div className="flex gap-0">
                                                        <div className="w-32 h-24 overflow-hidden shrink-0">
                                                            <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                                        </div>
                                                        <div className="p-4 flex-1 flex flex-col justify-between min-w-0">
                                                            <p className="font-semibold text-theme-primary text-sm leading-snug line-clamp-2 group-hover:text-primary-blue transition-colors">{r.title}</p>
                                                            <div className="flex items-center gap-3 text-xs text-theme-muted mt-2">
                                                                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(r.date)}</span>
                                                                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{r.readTime}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </GlassmorphicCard>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* News explorer */}
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <Newspaper className="h-5 w-5 text-primary-blue" />
                                    <h3 className="font-bold text-theme-primary">More News</h3>
                                </div>
                                <div className="space-y-4">
                                    {mockNews.filter(n => n.id !== id).slice(0, 5).map(n => (
                                        <Link key={n.id} to={`/news/${n.id}`} className="flex gap-3 group cursor-pointer">
                                            <div className="w-16 h-12 rounded-lg overflow-hidden shrink-0">
                                                <img src={n.image} alt={n.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-theme-primary group-hover:text-primary-blue transition-colors line-clamp-2 leading-snug">{n.title}</p>
                                                <p className="text-xs text-theme-muted mt-0.5">{formatDate(n.date)}</p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </GlassmorphicCard>

                            {/* Categories */}
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <Tag className="h-5 w-5 text-primary-blue" />
                                    <h3 className="font-bold text-theme-primary">Browse by Category</h3>
                                </div>
                                <div className="space-y-2">
                                    <Link to={`/news?category=${encodeURIComponent(article.category)}`}
                                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-primary-blue text-white text-sm">
                                        <span>{article.category}</span>
                                        <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Current</span>
                                    </Link>
                                    {otherCategories.slice(0, 5).map(cat => (
                                        <Link key={cat} to={`/news`}
                                            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 transition-all">
                                            <span>{cat}</span>
                                        </Link>
                                    ))}
                                </div>
                            </GlassmorphicCard>

                            {/* CTA */}
                            <GlassmorphicCard className="p-5 bg-gradient-to-br from-primary-blue/10 to-blue-600/10 border-primary-blue/20">
                                <h3 className="font-bold text-theme-primary mb-2">Stay Updated</h3>
                                <p className="text-sm text-theme-secondary mb-4">Never miss important NEET news and updates.</p>
                                <Link to="/contact">
                                    <button className="w-full py-2.5 bg-primary-blue hover:bg-blue-600 text-white text-sm font-semibold rounded-xl transition-colors">
                                        Get Notified
                                    </button>
                                </Link>
                            </GlassmorphicCard>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}
